/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { put, all, takeEvery, select, take, TakeEffect, race } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { AnyAction } from 'redux';
import {
  ActionRequest,
  ActionSuccess,
  Asset,
  ActionFail,
  XError,
  FileFormatToFileTypeMap,
  Constants,
  AssetsApi,
  ParentalControlSettings,
  HideUnsubscribed,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import AppConfig from '../../../services/AppConfig';

export enum ActionTypes {
  REQUEST = 'assets/REQUEST',
  SUCCESS = 'assets/SUCCESS',
  FAIL = 'assets/FAIL',
}

interface Meta {
  id: number;
  pageIndex: number;
  pageSize: number;
}

type Request = ActionRequest<ActionTypes.REQUEST, Meta>;
type Success = ActionSuccess<ActionTypes.SUCCESS, Asset[], Meta>;
type Fail = ActionFail<ActionTypes.FAIL, Meta>;

type Actions = Request | Success | Fail;

const Actions = {
  request: (meta: Meta): Request => ({ type: ActionTypes.REQUEST, meta }),
  success: (payload: Asset[], meta: Meta): Success => ({ type: ActionTypes.SUCCESS, payload, meta }),
  fail: (error: XError, meta: Meta): Fail => ({ type: ActionTypes.FAIL, error, meta }),
};

export const Creators = {
  loadAssets: (channelId: number, pageIndex: number, pageSize: number): Request =>
    Actions.request({ id: channelId, pageIndex, pageSize }),
};

export interface State {
  data?: Asset[];
  fetching?: boolean;
  fetched?: boolean;
  error?: Error;
  meta: Meta;
}

export interface ReducerState {
  [key: number]: State;
}

const initialState: ReducerState = {};

export default function assets(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.SUCCESS: {
      const newItems = action.payload || [];
      const { meta } = action;
      let mergedItem = (meta.pageIndex > 0 && state[meta.id]?.data) || [];
      mergedItem = [...mergedItem, ...newItems];
      return {
        ...state,
        [meta.id]: {
          ...state[meta.id],
          data: mergedItem,
          fetching: false,
          fetched: true,
        },
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          fetching: false,
          fetched: true,
          error: action.error,
        },
      };

    case ActionTypes.REQUEST:
      return {
        ...state,
        [action.meta.id]: {
          ...state[action.meta.id],
          fetching: true,
          fetched: false,
        },
      };

    default:
      return state;
  }
}

// SAGAS
const getNamespace = (id: number): string => {
  return `asset_list_${id}`;
};

export function* loadAssetsSaga(action: Actions): SagaIterator {
  const { meta } = action;
  try {
    const filesFormat: FileFormatToFileTypeMap = yield select(Ducks.Dms.Selectors.getFilesFormat);
    const parentalControlSettings: ParentalControlSettings = yield select(Ducks.Dms.Selectors.getParentalControl);

    const result = yield Ducks.Pagination.callPaginatedApi(
      getNamespace(meta.id),
      meta.pageIndex,
      meta.pageSize,
      Constants.ASSETS_API_PAGE_SIZE,
      AssetsApi.assetList,
      meta.id,
      filesFormat,
      parentalControlSettings,
      HideUnsubscribed[AppConfig.env.CHANNELS_UNSUBSCRIBED_VISIBILITY]
    );
    yield put(Actions.success(result, meta));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error), meta));
  }
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionTypes.REQUEST, loadAssetsSaga)]);
}

// SELECTORS
export const Selectors = {
  // isProcessed: (state: StoreState): boolean => state.menu.processed === true,
  // getError: (state: StoreState): XError | undefined => state.menu.error,
  completed: (state: StoreState, id: number): boolean => {
    const totalAssets = state.assets[id]?.data?.length || 0;
    const namespace = getNamespace(id);
    return (
      Ducks.Pagination.Selectors.isCompleted(state, namespace) &&
      Ducks.Pagination.Selectors.getTotal(state, namespace) === totalAssets
    );
  },
  getAssets: (state: StoreState, id: number): Asset[] | undefined => {
    return state.assets[id]?.data;
  },
};

// AUX
function takeAssets(actionType: ActionTypes.SUCCESS | ActionTypes.FAIL, channelId: number): TakeEffect {
  return take((act: AnyAction) => act.type === actionType && act.meta.id === channelId);
}

export function* loadAndGetAssetsSaga(channelId: number, numberOfAssets: number): SagaIterator<Asset[] | undefined> {
  yield put(Creators.loadAssets(channelId, 0, numberOfAssets));

  const [success] = yield race([takeAssets(ActionTypes.SUCCESS, channelId), takeAssets(ActionTypes.FAIL, channelId)]);

  if (success) {
    const result: Asset[] | undefined = yield select(Selectors.getAssets, channelId);
    if (result?.length) {
      return result.splice(0, numberOfAssets);
    }
  }
  return undefined;
}
